/**
 * levels.js — Level-System 2.0: "Die lange Reise" (SINGLE SOURCE OF TRUTH)
 * ─────────────────────────────────────────────────────────────────────────────
 * Design contract (real school data, multi-year progression):
 *   · Level 1–100. Cost to clear level n:  round(30 + 15 · n^1.35) XP.
 *   · Total to Level 100 ≈ 323.000 XP. With a realistic 1.000–1.500 XP/week
 *     for an active student (daily soft-cap: 300 full / 600 half / 900 max),
 *     the summit takes ≈ 4 YEARS. Level 2 lands on day one (hook), Level 50
 *     after about a year (identity), Level 95+ is legend territory.
 *   · Honor ranks (Ehrenränge) are bound to LEVEL, not raw XP — "Legende"
 *     is now a multi-year achievement instead of a two-week grind.
 *
 * The old system (60 XP flat per level, Legende at 1200 XP) is dead.
 * Existing XP maps losslessly onto the new curve (1200 XP ≈ Level 8).
 */

export const MAX_LEVEL = 100;

/** XP needed to clear level n (go from n → n+1). */
export const levelCost = (n) => Math.round(30 + 15 * Math.pow(n, 1.35));

/* cumulative thresholds: THRESH[l-1] = total XP where level l begins */
const THRESH = [0];
for (let n = 1; n < MAX_LEVEL; n++) THRESH.push(THRESH[n - 1] + levelCost(n));

export const TOTAL_XP_MAX = THRESH[MAX_LEVEL - 1];

export function getLevel(xp) {
  const v = Math.max(0, Math.floor(xp || 0));
  let lo = 1, hi = MAX_LEVEL;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (v >= THRESH[mid - 1]) lo = mid; else hi = mid - 1;
  }
  return lo;
}

/** Total XP at which `level` begins. */
export const levelStartXp = (level) => THRESH[Math.min(MAX_LEVEL, Math.max(1, level)) - 1];

/** Progress within the current level, 0–100. */
export function getLevelProgress(xp) {
  const lvl = getLevel(xp);
  if (lvl >= MAX_LEVEL) return 100;
  const start = THRESH[lvl - 1], end = THRESH[lvl];
  return Math.max(0, Math.min(100, ((xp - start) / (end - start)) * 100));
}

/** XP inside the current level + XP needed to clear it. */
export function getLevelSpan(xp) {
  const lvl = getLevel(xp);
  if (lvl >= MAX_LEVEL) return { current: 0, needed: 0, level: lvl };
  return { current: Math.floor(xp) - THRESH[lvl - 1], needed: THRESH[lvl] - THRESH[lvl - 1], level: lvl };
}

/* ── Ehrenränge — bound to LEVEL, the long-haul identity ────────────────── */
export const HONOR_RANKS = [
  { id: 1, level: 1,  name: "Sprössling",    emoji: "🌱", color: "#4ade80",
    description: "Jede Legende fängt mit Level 1 an. Willkommen auf der Reise." },
  { id: 2, level: 20, name: "Flammenträger", emoji: "🔥", color: "#fb923c",
    description: "Level 20 — Monate echter Arbeit. Die Flamme gehört jetzt dir." },
  { id: 3, level: 40, name: "Blitzdenker",   emoji: "⚡", color: "#facc15",
    description: "Level 40 — dein Wissen schlägt ein wie ein Blitz." },
  { id: 4, level: 60, name: "Diamantkopf",   emoji: "💎", color: "#38bdf8",
    description: "Level 60 — Jahre unter Druck. Genau so entstehen Diamanten." },
  { id: 5, level: 80, name: "Meister",       emoji: "🏔️", color: "#e2e8f0",
    description: "Level 80 — die Luft wird dünn. Nur wenige kommen so weit." },
  { id: 6, level: 95, name: "Legende",       emoji: "👑", color: "#c084fc",
    description: "Level 95+. Jahre der Disziplin. Eine echte, lebende Legende." },
];

export function getHonorRank(levelOrXp, isXp = false) {
  const lvl = isXp ? getLevel(levelOrXp) : levelOrXp;
  let rank = HONOR_RANKS[0];
  for (const r of HONOR_RANKS) if (lvl >= r.level) rank = r;
  return rank;
}

export function getNextHonorRank(levelOrXp, isXp = false) {
  const lvl = isXp ? getLevel(levelOrXp) : levelOrXp;
  return HONOR_RANKS.find((r) => r.level > lvl) || null;
}

/** Rough time estimate to reach a level, given weekly XP pace. */
export function estimateWeeksTo(targetLevel, xp, weeklyXp = 1200) {
  const missing = levelStartXp(targetLevel) - Math.max(0, xp);
  if (missing <= 0) return 0;
  return Math.ceil(missing / Math.max(100, weeklyXp));
}
